'use client';

import Birthday from '@/components/react-hook-form/Birthday';
import RegisterLayout from '../components/RegisterLayout';
import { useFormContext, Controller } from 'react-hook-form';
import type { FormSchema } from '@/app/register/schema';
import { useMemo } from 'react';
import { SelectItem } from '@/components/shadcn/Select';
import { registerTextStyle } from '@/app/register/constants';
import { buildUpdateUserProfileParams } from '../functions';
import { updateUserProfile } from '../../../actions/user';
import FirebasePageViewEvent from '@/components/firebase/PageViewEvent';
import { EVENTS } from '@/lib/firebase/analytics/events';

export const createDayOptions = () =>
  Array.from({ length: 31 }).map((_, i) => (
    <SelectItem key={i + 1} value={String(i + 1)}>
      {i + 1}日
    </SelectItem>
  ));

export const createMonthOptions = () =>
  Array.from({ length: 12 }).map((_, i) => (
    <SelectItem key={i + 1} value={String(i + 1)}>
      {i + 1}月
    </SelectItem>
  ));

export const createYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const startYear = 1960;

  return Array.from({ length: currentYear - startYear + 1 }).map((_, i) => (
    <SelectItem key={i} value={`${startYear + i}`}>
      {`${startYear + i}年`}
    </SelectItem>
  ));
};

export default function BirthDayForm() {
  const { watch, control, setValue } = useFormContext<FormSchema>();
  const YearElement = useMemo(() => createYearOptions(), []);
  const MonthElement = useMemo(() => createMonthOptions(), []);
  const DayElement = useMemo(() => createDayOptions(), []);

  const updateBirthday = () => {
    const birthdayYear = watch('birthday.year');
    const birthdayMonth = watch('birthday.month');
    const birthdayDay = watch('birthday.day');
    const birthday = {
      year: birthdayYear,
      month: birthdayMonth,
      day: birthdayDay
    };
    const params = buildUpdateUserProfileParams({ birthday });
    return updateUserProfile(params);
  };

  const handleChange = () => {
    // 生年月日の変更時に卒業年月を自動入力する
    const birthdayYear = watch('birthday.year');
    const birthdayMonth = watch('birthday.month');
    const birthdayDay = watch('birthday.day');

    // 生年月日が未入力、もしくは卒業年月が入力済みの場合、卒業年月を変更しない
    if (
      !birthdayYear ||
      !birthdayMonth ||
      !birthdayDay ||
      !!watch('graduationyear.year')
    ) {
      return;
    }

    const march = 3;
    const newGraduateAge = 23;

    const updatedGraduationYear =
      Number(birthdayMonth) > march
        ? Number(birthdayYear) + newGraduateAge
        : Number(birthdayYear) + newGraduateAge - 1;

    setValue('graduationyear', {
      year: String(updatedGraduationYear),
      month: String(march)
    });
  };

  return (
    <>
      <h3 className={registerTextStyle}>
        <span className="text-[20px] text-[#231717]">生年月日</span>
        を教えてください
      </h3>
      <RegisterLayout
        prev="username"
        next="prefecture"
        disabled={
          !watch('birthday.year') ||
          !watch('birthday.month') ||
          !watch('birthday.day')
        }
        updateProfile={updateBirthday}
      >
        <div className="mx-auto mt-[24px] flex max-w-[672px] justify-center gap-[20px]">
          <Controller
            control={control}
            name="birthday.year"
            render={({ field: { value, onChange } }) => (
              <Birthday
                value={value}
                onChange={val => {
                  onChange(val);
                  handleChange();
                }}
                optionElements={YearElement}
                date="年"
                placeholder="例 :  1998年"
                className="w-full"
              />
            )}
          />
          <Controller
            control={control}
            name="birthday.month"
            render={({ field: { value, onChange } }) => (
              <Birthday
                value={value}
                onChange={val => {
                  onChange(val);
                  handleChange();
                }}
                optionElements={MonthElement}
                date="月"
                placeholder="例 :  1月"
                className="w-full"
              />
            )}
          />
          <Controller
            control={control}
            name="birthday.day"
            render={({ field: { value, onChange } }) => (
              <Birthday
                value={value}
                onChange={val => {
                  onChange(val);
                  handleChange();
                }}
                optionElements={DayElement}
                date="日"
                placeholder="例 :  1日"
                className="w-full"
              />
            )}
          />
        </div>
      </RegisterLayout>

      {/* Firebaseイベント発火 */}
      <FirebasePageViewEvent eventName={EVENTS.registration_birthday_display} />
    </>
  );
}
